
import React from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Clock, Home, FileText, ArrowRight } from 'lucide-react';
import { DayViewModalProps } from '../../types';
import { formatDateES } from '../../lib/dateUtils';
import { useIsMobile, useIsTablet } from '../../hooks/useMediaQuery';
import { DS } from '../../lib/design-system';
import { logger } from '../../lib/logger';

export const DayViewModal: React.FC<DayViewModalProps> = ({
    isOpen,
    onClose,
    date,
    events,
    onOpenDetails,
    onOpenPropuesta,
    onOpenGestion,
    onAddReunion
}) => {
    const isMobile = useIsMobile();
    const isTablet = useIsTablet();

    // Note: isOpen check removed - parent AnimatePresence handles conditional rendering
    // This allows exit animations to play correctly

    const formattedDate = date ? formatDateES(date, { format: 'long', includeWeekday: true }) : '';
    const dayEvents = date ? events.filter(e => e.date === date) : [];

    // Use appropriate animation preset based on device
    const modalAnimation = isMobile ? DS.animations.modalMobile : DS.animations.modal;

    // Progressive modal sizing
    const getModalContainerPadding = () => {
        if (isMobile) return 'p-0';
        if (isTablet) return 'p-3';
        return 'p-4';
    };

    const getModalContentClass = () => {
        if (isMobile) return 'w-full h-full';
        if (isTablet) return 'rounded-2xl w-[92%] max-w-lg max-h-[90vh]';
        return 'rounded-3xl w-full max-w-xl max-h-[90vh]';
    };

    return (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center ${getModalContainerPadding()}`}>
            <motion.div
                {...DS.animations.backdrop}
                onClick={onClose}
                className="absolute inset-0 bg-[#111827]/60 backdrop-blur-sm"
            />
            <motion.div
                {...modalAnimation}
                className={`bg-white shadow-2xl relative z-10 overflow-hidden flex flex-col ${getModalContentClass()}`}
            >
                <div className="px-6 py-5 border-b border-[#D1D5DB] bg-[#F9FAFB] flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-[#111827] capitalize">{formattedDate}</h2>
                        <p className="text-xs text-[#6B7280] mt-0.5">{dayEvents.length} eventos registrados</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={onAddReunion}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#5D1828] hover:bg-[#7A2235] text-white rounded-lg text-xs font-medium transition-colors shadow-sm"
                        >
                            <Plus size={14} strokeWidth={2.5} />
                            Nueva Reunión
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-[#E5E7EB] rounded-full text-[#6B7280] hover:text-[#111827] transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>
                <div className="p-6 overflow-y-auto custom-scrollbar flex flex-col gap-4">
                    {dayEvents.length === 0 ? (
                        <div className="text-center py-8 text-[#6B7280] italic">No hay reuniones registradas para este día.</div>
                    ) : (
                        dayEvents.map((event, index) => (
                            <div key={index} className="bg-white border border-[#D1D5DB] rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow group relative flex-shrink-0">
                                {/* Accent Color Strip */}
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#5D1828] rounded-l-2xl" />
                                <div className="pl-3">
                                    {/* Header: Title + Time + Actions */}
                                    <div className="flex justify-between items-start gap-2 mb-3">
                                        <div
                                            onClick={() => onOpenDetails(event)}
                                            className="cursor-pointer flex-1 min-w-0"
                                        >
                                            <h3 className="font-bold text-[#111827] text-base group-hover:text-[#5D1828] transition-colors">{event.title}</h3>
                                            <p className="text-sm text-[#4B5563] flex items-center gap-1.5 mt-1 font-medium">
                                                <Clock size={14} className="flex-shrink-0" />
                                                <span>{event.time || 'Sin hora'}</span>
                                            </p>
                                        </div>
                                        <div className="flex gap-1 flex-shrink-0">
                                            <button
                                                onClick={() => {
                                                    logger.log('[DayViewModal] Opening propuesta for event:', { id: event.id, title: event.title, date: event.date, time: event.time });
                                                    onOpenPropuesta(event);
                                                }}
                                                className="p-1.5 rounded-lg bg-[#5D1828]/10 text-[#5D1828] hover:bg-[#5D1828]/20 transition-colors"
                                                title="Agregar Propuesta"
                                            >
                                                <Home size={14} />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    logger.log('[DayViewModal] Opening gestion for event:', { id: event.id, title: event.title, date: event.date, time: event.time });
                                                    onOpenGestion(event);
                                                }}
                                                className="p-1.5 rounded-lg bg-[#B89658]/10 text-[#B89658] hover:bg-[#B89658]/20 transition-colors"
                                                title="Agregar Gestión"
                                            >
                                                <FileText size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    {/* Footer: Type badge + Counts + Ver detalles */}
                                    <div className="flex items-center flex-wrap gap-2 pt-3 border-t border-[#E5E7EB]">
                                        <span className="text-xs font-medium px-2 py-1 rounded-md bg-[#F3F4F6] text-[#6B7280]">{event.type}</span>
                                        {((event.propuestasCount || 0) > 0 || (event.gestionesCount || 0) > 0) && (
                                            <div className="flex gap-3">
                                                {(event.propuestasCount || 0) > 0 && (
                                                    <div className="flex items-center gap-1 text-[#5D1828] text-xs font-medium" title="Propuestas vinculadas">
                                                        <Home size={14} />
                                                        <span>{event.propuestasCount}</span>
                                                    </div>
                                                )}
                                                {(event.gestionesCount || 0) > 0 && (
                                                    <div className="flex items-center gap-1 text-[#B89658] text-xs font-medium" title="Gestiones vinculadas">
                                                        <FileText size={14} />
                                                        <span>{event.gestionesCount}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => onOpenDetails(event)}
                                            className="ml-auto text-xs font-medium text-[#5D1828] hover:underline flex items-center gap-1"
                                        >
                                            Ver detalles <ArrowRight size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </motion.div>
        </div>
    );
};